import React, {
  forwardRef,
  useImperativeHandle,
  type ComponentType,
  type ReactNode,
} from 'react';
import c from 'classnames';

import useAriaDisclosure from '../../../hooks/useAriaDisclosure';

import './style.scss';

const ARIA_DISCLOSURE_ID_SEARCH_FORUM = 'ARIA_DISCLOSURE_ID_SEARCH_FORUM';

export interface SearchForumOverlayRef {
  toggleOverlay: (onlyClose?: boolean) => void;
}

export interface SearchForumOverlayProps {
  Trigger: ComponentType<ReturnType<typeof useAriaDisclosure>['toggleProps']>;
  className?: string;
  children: ReactNode;
}

// eslint-disable-next-line max-len
const SearchForumOverlay = forwardRef<SearchForumOverlayRef, SearchForumOverlayProps>((props, ref) => {
  const { Trigger, className, children } = props;

  const ariaDisclosure = useAriaDisclosure<HTMLDivElement>(ARIA_DISCLOSURE_ID_SEARCH_FORUM, {
    closeOnOutsideClick: true,
    closeOnEscape: true,
  });

  const classes = c('SearchForum__Overlay', className);

  useImperativeHandle(ref, () => ({
    toggleOverlay: (onlyClose?: boolean) => {
      if (onlyClose && !ariaDisclosure.isOpen) {
        return;
      }
      ariaDisclosure.toggleIsOpen();
    },
  }), [ariaDisclosure]);

  return (
    <div className={classes}>
      {!ariaDisclosure.isOpen && (
        <Trigger {...ariaDisclosure.toggleProps} />
      )}

      {ariaDisclosure.isOpen && (
        <>
          <div className="SearchForum__Overlay__panel" ref={ariaDisclosure.targetRef}>
            {children}
          </div>
          <div className="SearchForum__Overlay__background" />
        </>
      )}
    </div>
  );
});

export default SearchForumOverlay;
